import React, { useEffect, useState } from 'react';
import { TemplateResult } from './SurveyTemplates';
import { useTestTemplate } from './SurveyTemplates/TemplateEditor/TemplateEditorModal/useTestTemplate';
import SurveyResponse from '../util/SurveyResponse';
import { useSurveyTemplates } from '../util/useSurveyTemplates';
import { SurveyTemplateWithId } from '../../../services/SurveyService';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { CheckCircle, Eye, EyeOff, XCircle } from '@lifeomic/chromicons';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { defineMessages, useIntl } from 'react-intl';
import { permissions, enforce, Policy } from '../../../util/abac';
import { useCustomCompareEffect } from 'react-use';
import { useLocalStorageState } from '../../../hooks';
import { getTestProps } from '../../../util/testProps';
import clsx from 'clsx';
import _isEqual from 'lodash/isEqual';

type QuestionnaireResponse = fhir.QuestionnaireResponse;

export type PreviewVisibility = 'expanded' | 'collapsed';

type Props = {
  response: SurveyResponse;
  surveyId: string;
  /**
   * Reports the effective visibility (on mount and on toggle) so the parent
   * can widen its layout while the preview is expanded. Visibility state
   * itself stays here, persisted per survey in localStorage. Reports
   * 'collapsed' while the survey has no templates (nothing renders).
   */
  onVisibilityChange?: (visibility: PreviewVisibility) => void;
};

export const testIds = {
  root: 'surveyTemplatePreview-root',
  hideButton: 'surveyTemplatePreview-hide',
  showButton: 'surveyTemplatePreview-show',
};

const previewVisibilityStorageKey = (surveyId: string) =>
  `survey-template-preview-visibility:${surveyId}`;

const useStyles = makeStyles(theme => ({
  // The 400px flex-basis (vs the form's 600px in Survey.tsx) is what lets
  // the row wrap on narrow windows instead of overflowing horizontally.
  // The pane scrolls its own overflow so a long template never stretches
  // the row past the viewport.
  root: {
    flex: '2 1 400px',
    height: '100%',
    maxHeight: '100vh',
    overflowY: 'auto',
    position: 'sticky',
    top: 0,
  },
  collapsed: {
    flex: '0 0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  templateSelect: {
    margin: theme.spacing(2, 0),
  },
  status: {
    justifySelf: 'center',
  },
}));

const messages = defineMessages({
  title: {
    id: 'SurveyTemplatePreview.title',
    defaultMessage: 'Template Preview',
  },
  currentTemplate: {
    id: 'SurveyTemplatePreview.currentTemplate',
    defaultMessage: 'Current template',
  },
  previewIsUpToDate: {
    id: 'SurveyTemplatePreview.previewIsUpToDate',
    defaultMessage: 'Preview is up to date',
  },
  previewIsUpdating: {
    id: 'SurveyTemplatePreview.previewIsUpdating',
    defaultMessage: 'Preview is updating...',
  },
  previewIsError: {
    id: 'SurveyTemplatePreview.previewIsError',
    defaultMessage: 'Preview failed to generate, make a change to update.',
  },
  hidePreview: {
    id: 'SurveyTemplatePreview.hidePreview',
    defaultMessage: 'Hide template preview',
  },
  showPreview: {
    id: 'SurveyTemplatePreview.showPreview',
    defaultMessage: 'Show template preview',
  },
});

const Status = ({ type }: { type: 'current' | 'updating' | 'error' }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const icon = {
    current: <CheckCircle color={theme.palette.success.main} />,
    updating: <DotLoader size={0} />,
    error: <XCircle color={theme.palette.error.main} />,
  }[type];

  const message = {
    current: messages.previewIsUpToDate,
    updating: messages.previewIsUpdating,
    error: messages.previewIsError,
  }[type];

  return (
    <Box gap={1}>
      <Box width={2.5} height={2.5}>
        {icon}
      </Box>
      <Text size="subbody">{formatMessage(message)}</Text>
    </Box>
  );
};

export const SurveyTemplatePreview = ({
  response,
  surveyId,
  onVisibilityChange,
}: Props) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const [previewSrc, setPreviewSrc] = useState<string>();
  const [responseFhir, setResponseFhir] = useState<QuestionnaireResponse>();
  const [activeTemplate, setActiveTemplate] = useState<SurveyTemplateWithId>();
  // Collapsed by default: the preview pane crowds the survey form, so users
  // opt in via the eye icon. The choice persists per survey/user in
  // localStorage, and collapsed previews skip template rendering entirely.
  const [visibility, setVisibility] = useLocalStorageState<PreviewVisibility>(
    previewVisibilityStorageKey(surveyId),
    'collapsed',
  );
  const isCollapsed = visibility === 'collapsed';

  const { surveyTemplates } = useSurveyTemplates({ surveyId });
  const testResponse = useTestTemplate({
    template: isCollapsed
      ? undefined
      : activeTemplate?.templateConfig.template || '',
    response: isCollapsed ? undefined : responseFhir,
    surveyId,
    disableNotifications: true,
  });

  useCustomCompareEffect(
    () => setResponseFhir(response.toFhirQuestionnaireResponse()),
    [response],
    ([prevResponse], [nextResponse]) => {
      const prevFhir = prevResponse.toFhirQuestionnaireResponse();
      const nextFhir = nextResponse.toFhirQuestionnaireResponse();
      return _isEqual(prevFhir?.item, nextFhir?.item);
    },
  );

  useEffect(
    () => setActiveTemplate(current => current || surveyTemplates[0]),
    [surveyTemplates],
  );

  const rendersPreview = Boolean(activeTemplate);
  useEffect(() => {
    onVisibilityChange?.(rendersPreview ? visibility : 'collapsed');
  }, [onVisibilityChange, rendersPreview, visibility]);

  useEffect(() => {
    if (isCollapsed) {
      setPreviewSrc(undefined);
      return;
    }
    if (testResponse.testResponse?.result) {
      setPreviewSrc(testResponse.testResponse.result);
    }
  }, [isCollapsed, testResponse.testResponse?.result]);

  const status =
    testResponse.isFetching ||
    !testResponse.isFetched ||
    !testResponse.testResponse
      ? 'updating'
      : testResponse.testResponse.error
      ? 'error'
      : 'current';

  if (!activeTemplate) {
    return null;
  }

  if (isCollapsed) {
    return (
      <div
        className={clsx(classes.root, classes.collapsed)}
        {...getTestProps(testIds.root)}
      >
        <Tooltip title={formatMessage(messages.showPreview)}>
          <IconButton
            aria-label={formatMessage(messages.showPreview)}
            icon={Eye}
            size={0}
            onClick={() => setVisibility('expanded')}
            {...getTestProps(testIds.showButton)}
          />
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={classes.root} {...getTestProps(testIds.root)}>
      <div className={classes.header}>
        <Text size="headline">{formatMessage(messages.title)}</Text>
        <Tooltip title={formatMessage(messages.hidePreview)}>
          <IconButton
            aria-label={formatMessage(messages.hidePreview)}
            icon={EyeOff}
            size={0}
            onClick={() => setVisibility('collapsed')}
            {...getTestProps(testIds.hideButton)}
          />
        </Tooltip>
      </div>
      {surveyTemplates.length > 1 && (
        <Select
          label={formatMessage(messages.currentTemplate)}
          value={activeTemplate.templateId}
          className={classes.templateSelect}
          onChange={value => {
            const template = surveyTemplates.find(
              template => template.templateId === value,
            );
            if (template) {
              setActiveTemplate(template);
            }
          }}
        >
          {surveyTemplates.map(template => (
            <SelectOption
              key={template.templateId}
              title={template.name}
              value={template.templateId}
            />
          ))}
        </Select>
      )}
      {previewSrc && <TemplateResult templateResult={previewSrc} />}
      <Box className={classes.status}>
        <Status type={status} />
      </Box>
    </div>
  );
};

export const canViewTemplatePreview = (policy: Policy) => {
  const attributes = { resource: { type: 'survey-template' } };
  return (
    enforce(permissions.readData, policy, attributes) &&
    enforce(permissions.createData, policy, attributes)
  );
};
